import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSize } from 'ahooks';
import styles from './index.module.scss';
import { cancelFullscreen, goFullscreen } from '@/pages/WgqAllMaps/utils';
import Header from './components/Header';
import StationBlock from './components/StationBlock';
import SummaryCard from './components/SummaryCard';
import OverviewAggregate from './components/OverviewAggregate';
import StrategyPanel from './components/StrategyPanel';
import RightOverview from './components/RightOverview';

const setCompressedAirFitScale = (
  targetContainer?: HTMLDivElement,
  currentClientWidth?: number,
  currentClientHeight?: number,
  isFullScreen?: boolean,
  isStandardSize?: boolean
) => {
  if (!targetContainer || !currentClientWidth || !currentClientHeight) return;

  const designWidth = isStandardSize ? 1920 : 2970;
  const scale = currentClientWidth / designWidth;
  const containerHeight = isFullScreen
    ? document.documentElement.clientHeight
    : document.documentElement.clientHeight - 50;

  if (currentClientWidth >= designWidth) {
    targetContainer.style.width = `${designWidth}px`;
    targetContainer.style.height = `${containerHeight / scale}px`;
    targetContainer.style.transform = `scale3d(${scale}, ${scale}, ${scale})`;
    targetContainer.style.transformOrigin = 'top left';
    return;
  }

  targetContainer.style.width = '';
  targetContainer.style.height = '';
  targetContainer.style.transform = '';
  targetContainer.style.transformOrigin = '';
};

const CompressedAirOverview: React.FC = () => {
  const baseDivRef = useRef<HTMLDivElement>(null);
  const innerDivRef = useRef<HTMLDivElement>(null);
  const size = useSize(baseDivRef);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const isStandardSize = useMemo(() => {
    if (size?.width && size.width > 2560) {
      return false;
    }
    return true;
  }, [size?.width]);

  useEffect(() => {
    const handleResize = () => {
      if (size?.width && size?.height && size.width > 1920) {
        setCompressedAirFitScale(
          innerDivRef.current ?? undefined,
          size.width,
          size.height,
          isFullScreen,
          isStandardSize
        );
      } else if (innerDivRef.current) {
        innerDivRef.current.style.width = '';
        innerDivRef.current.style.height = '';
        innerDivRef.current.style.transform = '';
        innerDivRef.current.style.transformOrigin = '';
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullScreen, isStandardSize, size?.height, size?.width]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullScreen(document.fullscreenElement !== null);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const handleToggleFullscreen = () => {
    if (!baseDivRef.current) return;
    if (isFullScreen) {
      cancelFullscreen();
      return;
    }
    goFullscreen(baseDivRef.current);
  };

  return (
    <div id="full_screen_box" className={classNames(styles.page, 'compressorOverviewPage')} ref={baseDivRef}>
      <div className={styles.screen} ref={innerDivRef}>
        <Header isFullScreen={isFullScreen} onToggleFullscreen={handleToggleFullscreen} />

        <div className={styles.mainScroll}>
          <main className={styles.main}>
            <div className={styles.topRow}>
              <div className={styles.summaryPane}>
                <SummaryCard />
              </div>
              <div className={styles.aggregatePane}>
                <OverviewAggregate />
              </div>
              <div className={styles.overviewPane}>
                <RightOverview />
              </div>
            </div>

            <div className={styles.bottomRow}>
              <div className={styles.stationPane}>
                <StationBlock />
              </div>
              <div className={styles.strategyPane}>
                <StrategyPanel />
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default CompressedAirOverview;
